import TierNumbering from "./tier_numbering_controller";
import { allInPrice, netPayout } from "../utils/ticket_fees";

// Connects to data-controller="ticket-form"
export default class extends TierNumbering {
  static targets = ['priceField', 'effectiveDateField', 'archiveViewField', 'ticketPrice',
    'passedOnFee', 'absorbedFee',
    'buyerPay', 'organizerPay', 'organizerIncreaseReceive', 'organizerDecreaseReceive', 'donationQuantity', 'customDonationAmountFields',
    'accessScopeField', 'accessScopeRadio', 'accessScopeAddOnNote',
    'quantityField', 'quantityLabel', 'quantityHelp']

  static values = {
    // Fee rates sourced from config/initializers/constants.rb via the view, so the
    // organizer fee preview always matches what the backend actually charges.
    // Deliberately no numeric defaults: a fallback rate that disagrees with the
    // server is worse than a visible 0 from a missing data attribute.
    stubFeePercent: Number,
    stubFeeDollar: Number,
    stripeFeePercent: Number,
    stripeFeeCents: Number,
  }

  connect() {
    this.setArchiveField()
    if (this.hasPriceFieldTarget) this.updateBuyerTotal()
    if (this.hasDonationQuantityTarget) this.quantityControlVisibility()
    this.syncAccessScope()
    this.updateQuantityLabel()
  }

  // Re-label the quantity field to match the selected access scope so it's explicit
  // whether the number is per-date, a series total, or for the selected date. Skip
  // single events (no access-scope picker) — they keep the plain "Quantity" label.
  updateQuantityLabel() {
    if (!this.hasQuantityLabelTarget || !this.hasAccessScopeRadioTarget) return
    const copy = this.quantityCopy
    if (!copy) return

    const scope = this.selectedAccessScope
    const entry = copy[scope]
    if (!entry) return

    this.quantityLabelTarget.textContent = entry.label
    this.renderQuantityHelp(entry.help)
  }

  // Re-substitute the live quantity into the help text as the organizer types.
  // Single events have no scope-based help, so skip them.
  updateQuantityHelp() {
    if (!this.hasAccessScopeRadioTarget) return
    const copy = this.quantityCopy
    const entry = copy && copy[this.selectedAccessScope]
    if (entry) this.renderQuantityHelp(entry.help)
  }

  renderQuantityHelp(template) {
    if (!this.hasQuantityHelpTarget) return
    if (!template) { this.quantityHelpTarget.classList.add('d-none'); return }
    const count = (this.hasQuantityFieldTarget && this.quantityFieldTarget.value.trim()) || '0'
    this.quantityHelpTarget.textContent = template.replace('%{count}', count)
    this.quantityHelpTarget.classList.remove('d-none')
  }

  // Currently selected access scope (single_date_access / full_event_access /
  // custom_date). Defaults to single_date_access when no radio is present.
  get selectedAccessScope() {
    const checked = this.accessScopeRadioTargets.find((r) => r.checked)
    return checked ? checked.value : 'single_date_access'
  }

  // Localized label/help copy per scope, attached to the quantity field wrapper.
  get quantityCopy() {
    if (this._quantityCopy !== undefined) return this._quantityCopy
    const el = this.hasQuantityFieldTarget ? this.quantityFieldTarget.closest('[data-ticket-form-quantity-copy]') : null
    try {
      this._quantityCopy = el ? JSON.parse(el.dataset.ticketFormQuantityCopy) : null
    } catch (_e) {
      this._quantityCopy = null
    }
    return this._quantityCopy
  }

  // Add-ons (parking/vendor/merch) don't grant event access and aren't tied to a
  // date. When the organizer picks "Add-on", hide the access-scope picker, clear
  // any selected scope + effective date, and show a helper note instead. The model
  // also nils access_type for add-ons as a backstop.
  toggleAccessScope(event) {
    this.applyAccessScopeVisibility(event.target.value === 'add_on')
  }

  // Set the correct initial state on load (e.g. editing an existing add-on).
  syncAccessScope() {
    if (!this.hasAccessScopeFieldTarget) return
    const categorySelect = this.element.querySelector('select[name*="[category]"]')
    this.applyAccessScopeVisibility(categorySelect ? categorySelect.value === 'add_on' : false)
  }

  applyAccessScopeVisibility(isAddOn) {
    if (this.hasAccessScopeFieldTarget) this.accessScopeFieldTarget.classList.toggle('d-none', isAddOn)
    if (this.hasAccessScopeAddOnNoteTarget) this.accessScopeAddOnNoteTarget.classList.toggle('d-none', !isAddOn)

    if (isAddOn) {
      // Clear any selected access scope so no _type is submitted for an add-on.
      this.accessScopeRadioTargets.forEach((radio) => { radio.checked = false })
      if (this.hasEffectiveDateFieldTarget) {
        const dateInput = this.effectiveDateFieldTarget.querySelector('#ticket_effective_date')
        if (dateInput) dateInput.value = ''
        this.effectiveDateFieldTarget.classList.add('d-none')
      }
    }
  }

  updateBuyerTotal() {
    this.updateGroupPricingDiscount()
    this.updateEarlyBird()
    let ticketPrice = parseFloat(this.priceFieldTarget.value) || 0

    this.ticketPriceTargets.forEach(target => {
      target.innerHTML = this.formatCurrency(ticketPrice)
    })

    const consts = {
      stubFeePercent: this.stubFeePercentValue,
      stubFeeDollar: this.stubFeeDollarValue,
      stripeFeePercent: this.stripeFeePercentValue,
      stripeFeeCents: this.stripeFeeCentsValue,
    }

    // The two "View Detail" popovers preview different directions and need
    // different math (see utils/ticket_fees.js): Buyer Pays grosses the charge
    // up (matching Ticket#all_in_price and the real checkout total), You Pay
    // deducts the flat fees from the payout. Each popover's fee row is the
    // difference on its own side, so its rows always sum.
    const buyerTotal = ticketPrice > 0 ? allInPrice(ticketPrice, consts) : 0
    const netReceive = ticketPrice > 0 ? netPayout(ticketPrice, consts) : 0

    this.passedOnFeeTargets.forEach(target => {
      target.innerHTML = this.formatCurrency(buyerTotal - ticketPrice)
    })
    this.absorbedFeeTargets.forEach(target => {
      target.innerHTML = this.formatCurrency(ticketPrice - netReceive)
    })

    this.buyerPayTarget.innerHTML = this.formatCurrency(buyerTotal)
    this.organizerPayTarget.innerHTML = this.formatCurrency(ticketPrice)
    this.organizerIncreaseReceiveTarget.innerHTML = this.formatCurrency(ticketPrice)
    this.organizerDecreaseReceiveTarget.innerHTML = this.formatCurrency(netReceive)
  }

  showHideEffectiveDate() {
    if (event.currentTarget.value == 'custom_date') {
      this.effectiveDateFieldTarget.classList.remove('d-none')
    } else {
      this.effectiveDateFieldTarget.querySelector('#ticket_effective_date').value = ''
      this.effectiveDateFieldTarget.classList.add('d-none')
    }
  }

  setArchiveField() {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    if (urlParams.has('archive_view')) {
      this.archiveViewFieldTarget.value = urlParams.get('archive_view')
    }
  }

  updateGroupPricingDiscount() {
    this.tierNumberingControllers.forEach((tier) => {
      tier.calculateAndDisplayDiscount()
    })
  }

  updateEarlyBird() {
    this.earlyBirdPricingControllers.forEach((controller) => {
      controller.calculateSavings()
    })
  }

  quantityControlVisibility() {
    if (!this.hasDonationQuantityTarget) return
    const select = this.element.querySelector('select[name*="unlimited_quantity"]')
    if (!select) return
    const isUnlimited = select.value === 'true'
    if (isUnlimited) {
      this.donationQuantityTarget.classList.add('d-none')
      const input = this.donationQuantityTarget.querySelector('input[name*="quantity"]')
      if (input) input.value = ''
    } else {
      this.donationQuantityTarget.classList.remove('d-none')
    }
  }

  resetCustomDonationFields(event) {
    if (event.target.value === '0' && this.hasCustomDonationAmountFieldsTarget) {
      this.customDonationAmountFieldsTarget.querySelectorAll('input[type="number"]').forEach((input) => {
        input.value = ''
      })
    }
  }

  get modalController() {
    return this.application.controllers.find(controller => {
      return controller.context.identifier === 'modal';
    });
  }

  formatCurrency(amount) {
    return parseFloat(amount).toFixed(2)
  }

  get earlyBirdPricingControllers() {
    return this.application.controllers.filter(controller => {
      return controller.context.identifier === 'early-bird-pricing';
    });
  }
}
